import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Stethoscope, ArrowRight, AlertTriangle, CheckCircle2, ExternalLink, XCircle, LogOut } from 'lucide-react';

export const LungScreeningResults = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const { logout } = useAuth();
  const result = location.state?.result;

  if (!result) {
    return <Navigate to="/lung-screening" replace />;
  }

  const isEligible = result.is_eligible;
  const isSmoker = result.is_smoker !== false; // undefined means smoker for backwards compat
  const isExSmoker = result.has_quit === true; // Person quit smoking

  // Handle non-smoker case (never smoked or currently quit) - block access to the app
  if (!isSmoker) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="non-smoker-block">
        <div className="max-w-3xl mx-auto">
          <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-xl animate-scale-in" data-testid="non-smoker-card">
            {/* Header */}
            <div className={`${isExSmoker ? 'bg-green-100' : 'bg-blue-100'} p-8 sm:p-12 text-center`}>
              <div className={`inline-flex items-center gap-2 px-4 py-2 ${isExSmoker ? 'bg-green-200' : 'bg-blue-200'} rounded-full mb-6`}>
                {isExSmoker ? (
                  <CheckCircle2 className="w-5 h-5 text-green-700" />
                ) : (
                  <XCircle className="w-5 h-5 text-blue-700" />
                )}
                <span className={`font-medium ${isExSmoker ? 'text-green-700' : 'text-blue-700'}`}>
                  {isExSmoker 
                    ? (language === 'fr' ? 'Félicitations !' : 'Congratulations!')
                    : (language === 'fr' ? 'Accès refusé' : 'Access denied')}
                </span>
              </div>

              <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${isExSmoker ? 'bg-green-200' : 'bg-blue-200'}`}>
                {isExSmoker ? (
                  <CheckCircle2 className="w-12 h-12 text-green-700" />
                ) : (
                  <XCircle className="w-12 h-12 text-blue-700" />
                )}
              </div>

              <h2 className={`text-2xl sm:text-3xl font-bold ${isExSmoker ? 'text-green-800' : 'text-blue-800'} mb-4`}>
                {isExSmoker 
                  ? (language === 'fr' ? 'Vous avez déjà arrêté' : 'You have already quit')
                  : (language === 'fr' ? 'Application réservée aux fumeurs' : 'Application reserved for smokers')}
              </h2>
            </div>

            {/* Content */}
            <div className="p-8 sm:p-12">
              <div className={`p-6 rounded-2xl mb-8 ${isExSmoker ? 'bg-green-50 border-2 border-green-200' : 'bg-blue-50 border-2 border-blue-200'}`}>
                <p className={`text-lg leading-relaxed ${isExSmoker ? 'text-green-800' : 'text-blue-800'}`}>
                  {language === 'fr' 
                    ? result.message_fr
                    : result.message_en}
                </p>
              </div>

              {isExSmoker ? (
                <div className="bg-green-50 rounded-2xl p-6 mb-8 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    {language === 'fr' ? 'Bravo pour votre parcours !' : 'Well done on your journey!'}
                  </h4>
                  <p className="text-green-700 leading-relaxed">
                    {language === 'fr' 
                      ? "Continuez sur cette voie. Votre santé s'améliore chaque jour depuis que vous avez arrêté de fumer. Si vous connaissez quelqu'un qui souhaite arrêter, n'hésitez pas à lui recommander notre application."
                      : "Keep going on this path. Your health improves every day since you quit smoking. If you know someone who wants to quit, feel free to recommend our application."}
                  </p>
                </div>
              ) : (
                <div className="bg-green-50 rounded-2xl p-6 mb-8 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    {language === 'fr' ? 'Bonne nouvelle !' : 'Good news!'}
                  </h4>
                  <p className="text-green-700 leading-relaxed">
                    {language === 'fr' 
                      ? "Continuez à ne pas fumer, votre santé vous en remerciera. Si vous connaissez quelqu'un qui souhaite arrêter de fumer, n'hésitez pas à lui recommander notre application."
                      : "Keep not smoking, your health will thank you. If you know someone who wants to quit smoking, feel free to recommend our application."}
                  </p>
                </div>
              )}

              {/* Logout Button */}
              <Button 
                onClick={() => logout()}
                className={`w-full h-14 rounded-full shadow-lg text-lg ${
                  isExSmoker ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
                data-testid="logout-non-smoker-btn"
              >
                <LogOut className="mr-2 w-5 h-5" />
                {language === 'fr' ? 'Se déconnecter' : 'Log out'}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="lung-screening-results">
      <div className="max-w-3xl mx-auto">
        {/* Result Card */}
        <Card className="glass-card rounded-3xl overflow-hidden border-0 shadow-xl animate-scale-in" data-testid="result-card">
          {/* Header */}
          <div className={`${isEligible ? 'bg-orange-100' : 'bg-green-100'} p-8 sm:p-12 text-center`}>
            <div className={`inline-flex items-center gap-2 px-4 py-2 ${isEligible ? 'bg-orange-200' : 'bg-green-200'} rounded-full mb-6`}>
              <Stethoscope className={`w-5 h-5 ${isEligible ? 'text-orange-700' : 'text-green-700'}`} />
              <span className={`font-medium ${isEligible ? 'text-orange-700' : 'text-green-700'}`}>
                {language === 'fr' ? 'Résultat du dépistage' : 'Screening Result'}
              </span>
            </div>

            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${isEligible ? 'bg-orange-200' : 'bg-green-200'}`}>
              {isEligible ? (
                <AlertTriangle className={`w-12 h-12 text-orange-700`} />
              ) : (
                <CheckCircle2 className={`w-12 h-12 text-green-700`} />
              )}
            </div>

            <h2 className={`text-2xl sm:text-3xl font-bold ${isEligible ? 'text-orange-800' : 'text-green-800'} mb-4`}>
              {isEligible 
                ? (language === 'fr' ? 'Scanner recommandé' : 'CT Scan Recommended')
                : (language === 'fr' ? 'Non éligible au dépistage' : 'Not eligible for screening')}
            </h2>
          </div>

          {/* Content */}
          <div className="p-8 sm:p-12">
            {/* Message */}
            <div className={`p-6 rounded-2xl mb-8 ${isEligible ? 'bg-orange-50 border-2 border-orange-200' : 'bg-green-50 border-2 border-green-200'}`}>
              <p className={`text-lg leading-relaxed ${isEligible ? 'text-orange-800' : 'text-green-700 font-medium'}`} data-testid="screening-result-message">
                {isEligible 
                  ? (language === 'fr' 
                      ? 'Selon vos réponses il est préférable de prévoir un scanner thoracique pour le dépistage du cancer pulmonaire, cliquez sur ce bouton pour être dirigé vers le centre de radiologie le plus proche'
                      : 'Based on your answers, it is advisable to schedule a chest CT scan for lung cancer screening. Click this button to be directed to the nearest radiology center')
                  : (language === 'fr'
                      ? 'Selon vos réponses aucun dépistage de cancer pulmonaire n\'est à prévoir, cependant si vous fumez encore cette application peut vous aider à arrêter de fumer.'
                      : 'Based on your answers, no lung cancer screening is needed. However, if you still smoke, this app can help you quit.')}
              </p>
              {isEligible && (
                <a
                  href="https://www.depistage-cancer-poumon.fr/trouver-un-professionnel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center justify-center h-12 px-8 rounded-full bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-lg transition-colors"
                  data-testid="find-radiology-center-btn"
                >
                  <ExternalLink className="mr-2 w-5 h-5" />
                  {language === 'fr' ? 'Cliquez ici' : 'Click here'}
                </a>
              )}
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-8">
              <h4 className="font-semibold text-gray-900 mb-4">
                {language === 'fr' ? 'Vos informations' : 'Your information'}
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white rounded-xl">
                  <p className="text-xs text-gray-500">{language === 'fr' ? 'Âge' : 'Age'}</p>
                  <p className="text-lg font-bold text-gray-900">{result.age} {language === 'fr' ? 'ans' : 'years'}</p>
                </div>
                <div className="p-3 bg-white rounded-xl">
                  <p className="text-xs text-gray-500">{language === 'fr' ? 'Années de tabagisme' : 'Years smoking'}</p>
                  <p className="text-lg font-bold text-gray-900">{result.years_smoking} {language === 'fr' ? 'ans' : 'years'}</p>
                </div>
                <div className="p-3 bg-white rounded-xl">
                  <p className="text-xs text-gray-500">{language === 'fr' ? 'Cigarettes/jour' : 'Cigarettes/day'}</p>
                  <p className="text-lg font-bold text-gray-900">{result.cigarettes_per_day}</p>
                </div>
                <div className="p-3 bg-white rounded-xl">
                  <p className="text-xs text-gray-500">{language === 'fr' ? 'A arrêté' : 'Has quit'}</p>
                  <p className="text-lg font-bold text-gray-900">
                    {result.has_quit 
                      ? `${language === 'fr' ? 'Oui, depuis' : 'Yes, for'} ${result.years_since_quit} ${language === 'fr' ? 'ans' : 'years'}`
                      : (language === 'fr' ? 'Non' : 'No')}
                  </p>
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <Link to="/dashboard">
              <Button 
                className={`w-full h-14 rounded-full shadow-lg text-lg ${
                  isEligible ? 'bg-orange-600 hover:bg-orange-700' : 'bg-primary hover:bg-primary/90'
                }`}
                data-testid="continue-btn"
              >
                {language === 'fr' ? 'Continuer vers l\'application' : 'Continue to the app'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};
